import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/admin/LogoAndName.svg";
import Loading from "../../components/layout/Loading";
import { resetPassword } from "../../actions/AuthAction";
import { toast, ToastContainer } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const ResetPassword = () => {
  const PASSWORD_MAX_LENGTH = 20;
  const navigate = useNavigate();
  const [tempPassword, setTempPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({ temp: "", new: "", confirm: "" });
  const [isLoading, setIsLoading] = useState(false);
  const email = localStorage.getItem("username");
  const savedTempPassword = localStorage.getItem("tempPassword");

  const handleTempPasswordChange = (value) => {
    setTempPassword(value);
    setErrors((prev) => ({
      ...prev,
      temp:
        value && value !== savedTempPassword
          ? "Invalid temporary password"
          : "",
    }));
  };

  const handleNewPasswordChange = (value) => {
    setNewPassword(value);

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%?&])[A-Za-z\d@#$!%?&]{8,20}$/;

    setErrors((prev) => ({
      ...prev,
      new:
        value && !passwordRegex.test(value)
          ? `Password must be 8-${PASSWORD_MAX_LENGTH} chars, with uppercase, lowercase, number, and special character.`
          : "",
      confirm:
        confirmPassword && value !== confirmPassword
          ? "Passwords do not match."
          : "",
    }));
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    setErrors((prev) => ({
      ...prev,
      confirm: value && value !== newPassword ? "Passwords do not match." : "",
    }));
  };

  const handleReset = async () => {
    if (errors.temp || errors.new || errors.confirm) return;

    setIsLoading(true);

    try {
      const data = await resetPassword({
        username: email,
        newPassword,
        tokenOrTempPassword: savedTempPassword,
      });

      if (data.success) {
        toast.success(data.message || "Password reset successfully");
        localStorage.removeItem("tempPassword");

        setTimeout(() => navigate("/signin"), 1500);
      } else {
        // toast.error(data.message);
      }
    } catch (err) {
      // toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleReset();
  };

  return (
    <div className="flex items-center justify-center min-h-screen mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center space-y-6 w-full max-w-md">
        <img src={logo} alt="Logo" className="mx-auto p-2" />

        <div>
          <h2 className="text-text1 text-3xl leading-9 font-extrabold">
            Reset your password
          </h2>
          <p className="text-gray-500 text-[14px] leading-tight mt-2 ">
            Enter the temporary password sent to {email} to verify your identity
            and reset your password.
          </p>
        </div>

        {!isLoading ? (
          <div className="space-y-6">
            {/* Temporary Password */}
            <div className="text-left">
              <div className="relative">
                <input
                  type={showTempPassword ? "text" : "password"}
                  placeholder="Temporary password"
                  className={`base_input w-full p-2 pr-10 border rounded-md ${
                    errors.temp ? "border-red-500" : "border-gray-300"
                  }`}
                  value={tempPassword}
                  onChange={(e) => handleTempPasswordChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  onClick={() => setShowTempPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={
                    showTempPassword
                      ? "Hide temporary password"
                      : "Show temporary password"
                  }
                >
                  {showTempPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {errors.temp && (
                <p className="text-[#e23131] text-sm mt-1">{errors.temp}</p>
              )}
            </div>

            {/* New Password */}
            <div className="text-left">
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New password"
                  className={`base_input w-full p-2 pr-10 border rounded-md ${
                    errors.new ? "border-red-500" : "border-gray-300"
                  }`}
                  value={newPassword}
                  onChange={(e) => handleNewPasswordChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={PASSWORD_MAX_LENGTH}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={
                    showNewPassword ? "Hide new password" : "Show new password"
                  }
                >
                  {showNewPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {errors.new && (
                <p className="text-[#e23131] text-sm mt-1">{errors.new}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="text-left">
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  className={`base_input w-full p-2 pr-10 border rounded-md ${
                    errors.confirm ? "border-red-500" : "border-gray-300"
                  }`}
                  value={confirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={PASSWORD_MAX_LENGTH}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirmPassword ? (
                    <Eye size={18} />
                  ) : (
                    <EyeOff size={18} />
                  )}
                </button>
              </div>
              {errors.confirm && (
                <p className="text-[#e23131] text-sm mt-1">{errors.confirm}</p>
              )}
            </div>

            {/* Reset Button */}
            <button
              className={`bg-primary text-text1 rounded-md p-4 w-full mt-4 font-medium ${
                !tempPassword || !newPassword || !confirmPassword
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleReset}
              disabled={!tempPassword || !newPassword || !confirmPassword}
            >
              Confirm & Reset
            </button>
          </div>
        ) : (
          <Loading />
        )}
        <ToastContainer
          position="top-right z-40"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
        />
      </div>
    </div>
  );
};

export default ResetPassword;
